import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import * as XLSX from "xlsx";
import { DateRangePicker } from "react-date-range";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import MainLayoutWithoutFooter from "./MainLayoutWithoutFooter";
import { callAPI } from "../utils/apiUtils";
import { apiUrls } from "../utils/apiUrls";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "../styles/calander.css";
import "../styles/teamTimesheet.css";

/**
 * Manager view of the Timesheet page - every staff member's shifts, filterable by date range and
 * staff member, with a per-staff summary and an Excel export (a month at a time is the common
 * payroll case, hence the month stepper). Shown instead of the personal Timesheet when the PIN
 * belongs to someone with `staff.view` (TimesheetController::TEAM_PERMISSION).
 *
 * `employee` is the validate-pin payload; its `team_token` is what the team-* endpoints
 * authenticate the viewer with - never the bare employee_id.
 */

const QUICK_RANGES = [
  { key: "today", label: "Today", get: () => [moment().startOf("day"), moment().endOf("day")] },
  { key: "yesterday", label: "Yesterday", get: () => [moment().subtract(1, "day").startOf("day"), moment().subtract(1, "day").endOf("day")] },
  { key: "week", label: "This week", get: () => [moment().startOf("isoWeek"), moment().endOf("day")] },
  { key: "7", label: "Last 7 days", get: () => [moment().subtract(6, "days").startOf("day"), moment().endOf("day")] },
  { key: "month", label: "This month", get: () => [moment().startOf("month"), moment().endOf("day")] },
  { key: "lastMonth", label: "Last month", get: () => [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")] },
];

// A closed shift this long almost always means someone forgot to clock out.
const LONG_SHIFT_SECONDS = 12 * 3600;

const fullName = (p) => `${p.first_name || ""} ${p.last_name || ""}`.trim() || "Unknown";
const initials = (p) => `${(p.first_name || "?")[0]}${(p.last_name || "")[0] || ""}`.toUpperCase();

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return "-";
  const totalMinutes = Math.floor(Math.max(0, seconds) / 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
};

const toHours = (seconds) => Math.round((seconds / 3600) * 100) / 100;

const formatMoney = (value) =>
  `$${(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TeamTimesheet = ({ employee }) => {
  const teamToken = employee.team_token;
  const canViewPay = !!employee.can_view_pay;

  const [staff, setStaff] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [range, setRange] = useState(() => {
    const [s, e] = QUICK_RANGES.find((r) => r.key === "month").get();
    return { start: s, end: e };
  });
  const [activeQuick, setActiveQuick] = useState("month");
  const [showPicker, setShowPicker] = useState(false);
  const [pickerSelection, setPickerSelection] = useState(null);
  const [staffFilter, setStaffFilter] = useState("all");
  const [view, setView] = useState("summary");
  const [, setTick] = useState(0);
  const pickerRef = useRef(null);

  // Re-render every 30s so "on shift" durations keep counting up.
  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!showPicker) return undefined;
    const onClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowPicker(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [showPicker]);

  const loadStaff = async () => {
    try {
      const res = await callAPI(`${apiUrls.TIMESHEET_TEAM_STAFF}?team_token=${encodeURIComponent(teamToken)}`, {}, "GET");
      setStaff(res.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load staff");
    }
  };

  const loadShifts = async () => {
    setLoading(true);
    try {
      const res = await callAPI(
        `${apiUrls.TIMESHEET_TEAM_HISTORY}?team_token=${encodeURIComponent(teamToken)}` +
          `&start_date=${range.start.format("YYYY-MM-DD")}&end_date=${range.end.format("YYYY-MM-DD")}`,
        {},
        "GET"
      );
      setShifts(res.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load timesheets");
      setShifts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadShifts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const applyQuick = (key) => {
    const [s, e] = QUICK_RANGES.find((r) => r.key === key).get();
    setRange({ start: s, end: e });
    setActiveQuick(key);
  };

  // The month stepper always lands on a whole calendar month - the payroll export unit.
  const stepMonth = (delta) => {
    const base = range.start.clone().startOf("month").add(delta, "month");
    const isCurrentMonth = base.isSame(moment(), "month");
    const end = isCurrentMonth ? moment().endOf("day") : base.clone().endOf("month");
    setRange({ start: base, end });
    setActiveQuick(isCurrentMonth ? "month" : base.isSame(moment().subtract(1, "month"), "month") ? "lastMonth" : "custom");
  };

  const isWholeMonth =
    range.start.isSame(range.start.clone().startOf("month"), "day") &&
    (range.end.isSame(range.start.clone().endOf("month"), "day") ||
      (range.start.isSame(moment(), "month") && range.end.isSame(moment(), "day")));
  const canStepForward = range.start.clone().add(1, "month").startOf("month").isSameOrBefore(moment(), "day");

  const staffById = useMemo(() => {
    const map = {};
    staff.forEach((s) => { map[s.employee_id] = s; });
    return map;
  }, [staff]);

  const now = moment();
  const enrichedShifts = useMemo(
    () =>
      shifts.map((row) => {
        const person = staffById[row.employee_id];
        const isOpen = !row.check_out;
        // check_in has no timezone, so a browser behind the store's timezone sees it in the future - clamp to 0.
        const openSeconds = isOpen && row.check_in ? Math.max(0, now.diff(moment(row.check_in), "seconds")) : null;
        // An "open" shift that started before today was almost certainly never clocked out.
        const isStale = isOpen && row.check_in && !moment(row.check_in).isSame(now, "day");
        return {
          ...row,
          name: fullName(row),
          role: person?.role || (row.employee_active ? null : "Former staff"),
          hourlyRate: person?.hourly_rate ?? null,
          isOpen,
          isStale,
          isLong: !isOpen && row.duration_seconds > LONG_SHIFT_SECONDS,
          liveSeconds: isOpen ? openSeconds : row.duration_seconds,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shifts, staffById, now.format("YYYY-MM-DD HH:mm")]
  );

  const visibleShifts = useMemo(
    () => (staffFilter === "all" ? enrichedShifts : enrichedShifts.filter((s) => String(s.employee_id) === String(staffFilter))),
    [enrichedShifts, staffFilter]
  );

  // Totals only count closed shifts - an open shift has no end time to pay against yet.
  const summary = useMemo(() => {
    const byStaff = {};
    visibleShifts.forEach((s) => {
      if (!byStaff[s.employee_id]) {
        byStaff[s.employee_id] = {
          employee_id: s.employee_id,
          first_name: s.first_name,
          last_name: s.last_name,
          name: s.name,
          role: s.role,
          hourlyRate: s.hourlyRate,
          seconds: 0,
          shifts: 0,
          openShifts: 0,
          flagged: 0,
          days: new Set(),
        };
      }
      const entry = byStaff[s.employee_id];
      entry.shifts += 1;
      entry.days.add(moment(s.shift_date).format("YYYY-MM-DD"));
      if (s.isOpen) entry.openShifts += 1;
      else entry.seconds += s.duration_seconds || 0;
      if (s.isLong || s.isStale) entry.flagged += 1;
    });
    const rows = Object.values(byStaff)
      .map((r) => ({
        ...r,
        days: r.days.size,
        closedShifts: r.shifts - r.openShifts,
        pay: r.hourlyRate !== null && r.hourlyRate !== undefined ? toHours(r.seconds) * r.hourlyRate : null,
      }))
      .sort((a, b) => b.seconds - a.seconds);

    const totalSeconds = rows.reduce((acc, r) => acc + r.seconds, 0);
    const totalPay = rows.reduce((acc, r) => acc + (r.pay || 0), 0);
    const flagged = rows.reduce((acc, r) => acc + r.flagged, 0);
    return { rows, totalSeconds, totalPay, flagged, maxSeconds: rows[0]?.seconds || 0 };
  }, [visibleShifts]);

  const selectedStaff = staffFilter === "all" ? null : staffById[staffFilter] || summary.rows[0];

  const exportExcel = () => {
    if (!visibleShifts.length) {
      toast.info("Nothing to export for this period");
      return;
    }

    const period = `${range.start.format("DD MMM YYYY")} - ${range.end.format("DD MMM YYYY")}`;
    const who = selectedStaff ? fullName(selectedStaff) : "All staff";

    const summaryHeader = ["Staff", "Role", "Days worked", "Shifts", "Total hours", "Total (h:mm)"];
    if (canViewPay) summaryHeader.push("Hourly rate", "Est. pay");
    summaryHeader.push("Open shifts", "Needs review");
    const summarySheet = [
      ["Timesheet summary"],
      ["Period", period],
      ["Staff", who],
      ["Exported", moment().format("DD MMM YYYY hh:mm A")],
      [],
      summaryHeader,
      ...summary.rows.map((r) => {
        const line = [r.name, r.role || "", r.days, r.shifts, toHours(r.seconds), formatDuration(r.seconds)];
        if (canViewPay) line.push(r.hourlyRate ?? "", r.pay !== null ? Math.round(r.pay * 100) / 100 : "");
        line.push(r.openShifts, r.flagged);
        return line;
      }),
      [],
      (() => {
        const total = ["Total", "", "", summary.rows.reduce((a, r) => a + r.shifts, 0), toHours(summary.totalSeconds), formatDuration(summary.totalSeconds)];
        if (canViewPay) total.push("", Math.round(summary.totalPay * 100) / 100);
        return total;
      })(),
    ];

    const shiftSheet = [
      ["Date", "Day", "Staff", "Role", "Clock-in", "Clock-out", "Hours", "Duration (h:mm)", "Note"],
      ...[...visibleShifts]
        .sort((a, b) => (a.name === b.name ? moment(a.check_in).diff(moment(b.check_in)) : a.name.localeCompare(b.name)))
        .map((s) => [
          moment(s.shift_date).format("DD/MM/YYYY"),
          moment(s.shift_date).format("ddd"),
          s.name,
          s.role || "",
          s.check_in ? moment(s.check_in).format("hh:mm A") : "",
          s.check_out ? moment(s.check_out).format("hh:mm A") : "",
          s.isOpen ? "" : toHours(s.duration_seconds || 0),
          s.isOpen ? "" : formatDuration(s.duration_seconds),
          s.isStale ? "Missing clock-out" : s.isOpen ? "Still on shift" : s.isLong ? "Over 12 hours" : "",
        ]),
    ];

    const workbook = XLSX.utils.book_new();
    const ws1 = XLSX.utils.aoa_to_sheet(summarySheet);
    ws1["!cols"] = summaryHeader.map((_, i) => ({ wch: i === 0 ? 24 : 14 }));
    const ws2 = XLSX.utils.aoa_to_sheet(shiftSheet);
    ws2["!cols"] = [12, 6, 24, 14, 11, 11, 8, 14, 18].map((wch) => ({ wch }));
    XLSX.utils.book_append_sheet(workbook, ws1, "Summary");
    XLSX.utils.book_append_sheet(workbook, ws2, "Shifts");

    const periodSlug = isWholeMonth ? range.start.format("MMM-YYYY") : `${range.start.format("YYYYMMDD")}-${range.end.format("YYYYMMDD")}`;
    const whoSlug = who.replace(/[^a-z0-9]+/gi, "-");
    XLSX.writeFile(workbook, `Timesheet_${whoSlug}_${periodSlug}.xlsx`);
  };

  const rangeLabel = range.start.isSame(range.end, "day")
    ? range.start.format("ddd, DD MMM YYYY")
    : `${range.start.format("DD MMM YYYY")} – ${range.end.format("DD MMM YYYY")}`;

  return (
    <MainLayoutWithoutFooter>
      <div className="pageContent timesheetsPage teamTs pb-4">
        <div className="container">

          {/* Header */}
          <div className="tsHeader">
            <div>
              <h2 className="lgTitle mb-1">Team Timesheets</h2>
              <p className="tsMuted mb-0">
                Signed in as <strong>{fullName(employee)}</strong>
                {employee.role ? ` · ${employee.role}` : ""}
              </p>
            </div>
            <div className="tsHeaderActions">
              <button type="button" className="tsGhostBtn" onClick={() => navigate("/clockin-clockout")}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Back
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="tsFilterBar">
            <div className="tsFilterRow">
              <div className="tsMonthStepper">
                <button type="button" onClick={() => stepMonth(-1)} aria-label="Previous month">‹</button>
                <span>{isWholeMonth ? range.start.format("MMMM YYYY") : "Month"}</span>
                <button type="button" onClick={() => stepMonth(1)} disabled={!canStepForward} aria-label="Next month">›</button>
              </div>

              <div className="tsDateWrap" ref={pickerRef}>
                <button
                  type="button"
                  className={`tsDateBtn ${activeQuick === "custom" ? "active" : ""}`}
                  onClick={() => {
                    setPickerSelection({ startDate: range.start.toDate(), endDate: range.end.toDate(), key: "selection" });
                    setShowPicker(!showPicker);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
                    <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  {rangeLabel}
                </button>
                {showPicker && pickerSelection && (
                  <div className="tsPickerPop">
                    <DateRangePicker
                      ranges={[pickerSelection]}
                      onChange={(item) => setPickerSelection(item.selection)}
                      moveRangeOnFirstSelection={false}
                      maxDate={new Date()}
                    />
                    <div className="tsPickerActions">
                      <button type="button" className="tsGhostBtn" onClick={() => setShowPicker(false)}>Cancel</button>
                      <button
                        type="button"
                        className="tsPrimaryBtn"
                        onClick={() => {
                          setRange({
                            start: moment(pickerSelection.startDate).startOf("day"),
                            end: moment(pickerSelection.endDate).endOf("day"),
                          });
                          setActiveQuick("custom");
                          setShowPicker(false);
                        }}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="tsSelectWrap">
                <select value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)} aria-label="Filter by staff">
                  <option value="all">All staff ({staff.length})</option>
                  {staff.map((s) => (
                    <option key={s.employee_id} value={s.employee_id}>
                      {fullName(s)}{s.role ? ` — ${s.role}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <button type="button" className="tsPrimaryBtn tsExportBtn" onClick={exportExcel} disabled={loading}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 3v12m0 0l-5-5m5 5l5-5M4 21h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Download Excel
              </button>
            </div>

            <div className="tsChips">
              {QUICK_RANGES.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  className={`tsChip ${activeQuick === r.key ? "active" : ""}`}
                  onClick={() => applyQuick(r.key)}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* KPIs */}
          <div className="tsKpis">
            <div className="tsKpi">
              <span className="tsKpiLabel">Total hours</span>
              <span className="tsKpiValue">{loading ? "—" : formatDuration(summary.totalSeconds)}</span>
              <span className="tsKpiSub">{loading ? "" : `${toHours(summary.totalSeconds)} hrs`}</span>
            </div>
            <div className="tsKpi">
              <span className="tsKpiLabel">Shifts</span>
              <span className="tsKpiValue">{loading ? "—" : visibleShifts.length}</span>
              <span className="tsKpiSub">
                {loading ? "" : `${summary.rows.length} staff worked`}
              </span>
            </div>
            {canViewPay ? (
              <div className="tsKpi">
                <span className="tsKpiLabel">Est. wages</span>
                <span className="tsKpiValue">{loading ? "—" : formatMoney(summary.totalPay)}</span>
                <span className="tsKpiSub">From hourly rates</span>
              </div>
            ) : (
              <div className="tsKpi">
                <span className="tsKpiLabel">Avg shift</span>
                <span className="tsKpiValue">
                  {loading ? "—" : formatDuration(
                    summary.rows.reduce((a, r) => a + r.closedShifts, 0)
                      ? summary.totalSeconds / summary.rows.reduce((a, r) => a + r.closedShifts, 0)
                      : 0
                  )}
                </span>
                <span className="tsKpiSub">Completed shifts</span>
              </div>
            )}
            <div className={`tsKpi ${summary.flagged ? "warn" : ""}`}>
              <span className="tsKpiLabel">Needs review</span>
              <span className="tsKpiValue">{loading ? "—" : summary.flagged}</span>
              <span className="tsKpiSub">Missed clock-outs / 12h+</span>
            </div>
          </div>

          {/* Active filter strip */}
          {selectedStaff && (
            <div className="tsFilterStrip">
              Showing <strong>{fullName(selectedStaff)}</strong> only
              <button type="button" onClick={() => setStaffFilter("all")}>Show all staff ✕</button>
            </div>
          )}

          {/* View tabs */}
          <div className="tsTabs" role="tablist">
            <button type="button" role="tab" aria-selected={view === "summary"} className={view === "summary" ? "active" : ""} onClick={() => setView("summary")}>
              Summary by staff
            </button>
            <button type="button" role="tab" aria-selected={view === "shifts"} className={view === "shifts" ? "active" : ""} onClick={() => setView("shifts")}>
              All shifts <span className="tsCount">{visibleShifts.length}</span>
            </button>
          </div>

          <div className="cardStyle tsCard">
            {loading ? (
              <div className="tsSkeleton">
                {[0, 1, 2, 3].map((i) => <div key={i} className="tsSkeletonRow" />)}
              </div>
            ) : visibleShifts.length === 0 ? (
              <div className="tsEmpty">
                <div className="tsEmptyIcon">🕒</div>
                <h3>No shifts in this period</h3>
                <p className="tsMuted">Try a wider date range{selectedStaff ? " or show all staff" : ""}.</p>
              </div>
            ) : view === "summary" ? (
              <div className="table-responsive">
                <table className="table tableStyle2 table-borderless tsTable mb-0">
                  <thead>
                    <tr>
                      <th>Staff</th>
                      <th className="text-center">Days</th>
                      <th className="text-center">Shifts</th>
                      <th>Total hours</th>
                      {canViewPay && <th className="text-end">Rate</th>}
                      {canViewPay && <th className="text-end">Est. pay</th>}
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {summary.rows.map((r) => (
                      <tr key={r.employee_id} className="tsRowClick" onClick={() => { setStaffFilter(String(r.employee_id)); setView("shifts"); }}>
                        <td>
                          <div className="tsStaffCell">
                            <span className="tsAvatar">{initials(r)}</span>
                            <span>
                              <span className="tsPersonName">{r.name}</span>
                              <span className="tsPersonMeta">{r.role || "—"}</span>
                            </span>
                          </div>
                        </td>
                        <td className="text-center">{r.days}</td>
                        <td className="text-center">
                          {r.shifts}
                          {r.openShifts > 0 && <span className="tsBadge live ms-2">{r.openShifts} open</span>}
                        </td>
                        <td style={{ minWidth: 180 }}>
                          <div className="tsHours">
                            <strong>{formatDuration(r.seconds)}</strong>
                            <div className="tsBar">
                              <span style={{ width: `${summary.maxSeconds ? (r.seconds / summary.maxSeconds) * 100 : 0}%` }} />
                            </div>
                          </div>
                        </td>
                        {canViewPay && <td className="text-end">{r.hourlyRate !== null ? `${formatMoney(r.hourlyRate)}/h` : <span className="tsMuted">Not set</span>}</td>}
                        {canViewPay && <td className="text-end"><strong>{r.pay !== null ? formatMoney(r.pay) : "—"}</strong></td>}
                        <td className="text-end">
                          {r.flagged > 0 && <span className="tsBadge warn me-2">{r.flagged} to review</span>}
                          <span className="tsChevron">›</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {summary.rows.length > 1 && (
                    <tfoot>
                      <tr>
                        <td><strong>Total</strong></td>
                        <td />
                        <td className="text-center"><strong>{visibleShifts.length}</strong></td>
                        <td><strong>{formatDuration(summary.totalSeconds)}</strong></td>
                        {canViewPay && <td />}
                        {canViewPay && <td className="text-end"><strong>{formatMoney(summary.totalPay)}</strong></td>}
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table tableStyle2 table-borderless tsTable mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      {!selectedStaff && <th>Staff</th>}
                      <th>Clock-in</th>
                      <th>Clock-out</th>
                      <th>Duration</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleShifts.map((s) => (
                      <tr key={s.id}>
                        <td>
                          <span className="tsPersonName">{moment(s.shift_date).format("DD MMM YYYY")}</span>
                          <span className="tsPersonMeta">{moment(s.shift_date).format("dddd")}</span>
                        </td>
                        {!selectedStaff && (
                          <td>
                            <div className="tsStaffCell">
                              <span className="tsAvatar sm">{initials(s)}</span>
                              <span>
                                <span className="tsPersonName">{s.name}</span>
                                <span className="tsPersonMeta">{s.role || "—"}</span>
                              </span>
                            </div>
                          </td>
                        )}
                        <td>{s.check_in ? moment(s.check_in).format("hh:mm A") : "-"}</td>
                        <td>
                          {s.check_out
                            ? moment(s.check_out).format(moment(s.check_out).isSame(moment(s.check_in), "day") ? "hh:mm A" : "DD MMM hh:mm A")
                            : <span className="tsMuted">—</span>}
                        </td>
                        <td><strong>{formatDuration(s.liveSeconds)}</strong></td>
                        <td className="text-end">
                          {s.isStale ? (
                            <span className="tsBadge warn">Missing clock-out</span>
                          ) : s.isOpen ? (
                            <span className="tsBadge live"><span className="tsDot on" /> On shift</span>
                          ) : s.isLong ? (
                            <span className="tsBadge warn">Over 12h</span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {!loading && visibleShifts.some((s) => s.isOpen) && (
            <p className="tsFootnote">Open shifts are not counted in totals or the export until they're clocked out.</p>
          )}
        </div>
      </div>
    </MainLayoutWithoutFooter>
  );
};

export default TeamTimesheet;
